export const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api`;
const API_KEY = import.meta.env.VITE_API_KEY


/**
 * Get providers list for admin
 * @param {string} token - JWT authentication token
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} - Providers list with pagination
 */
export async function getAdminProviders(token, params = {}) {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.status_filter) queryParams.append('status_filter', params.status_filter);
    if (params.verification_pending !== undefined) {
      queryParams.append('verification_pending', params.verification_pending);
    }

    const apiUrl = `${API_BASE_URL}/providers/protected/admin/providers?${queryParams.toString()}`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const textResponse = await response.text();
          if (textResponse) {
            errorMessage = `${errorMessage}: ${textResponse}`;
          }
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      return {
        success: false,
        error: errorMessage
      };
    }

    const data = await response.json();
    return {
      success: true,
      providers: data.providers || [],
      total: data.total || 0,
      page: data.page || 1,
      page_size: data.page_size || 20,
      total_pages: data.total_pages || 0
    };
  } catch (error) {
    console.error('Error fetching admin providers:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Update provider status (approve/reject/suspend)
 * @param {string} token - JWT authentication token
 * @param {Object} data - Status update data
 * @returns {Promise<Object>} - Update result
 */
export async function updateProviderStatus(token, data) {
  try {
    const apiUrl = `${API_BASE_URL}/providers/protected/admin/providers/status`;
    
    const response = await fetch(apiUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const textResponse = await response.text();
          if (textResponse) {
            errorMessage = `${errorMessage}: ${textResponse}`;
          }
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      return {
        success: false,
        error: errorMessage
      };
    }

    // Backend returns StatusCode::OK with no body
    return {
      success: true
    };
  } catch (error) {
    console.error('Error updating provider status:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Admin: Trigger translation for a provider
 * @param {string} token - JWT authentication token
 * @param {string} providerId - Provider ID to translate
 * @returns {Promise<Object>} - Success/error response
 */
export async function adminTranslateProvider(token, providerId) {
  console.log('adminTranslateProvider called with:', { providerId, token: token ? 'present' : 'missing' });
    const url = `${API_BASE_URL}/providers/protected/admin/providers/${providerId}/translate`;
  console.log('Fetching URL:', url);

  try {
    const response = await fetch(url, { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      return {
        success: false,
        error: errorMessage
      };
    }

    const data = await response.json();
    return {
      success: true,
      message: data.message || 'Provider translated successfully'
    };
  } catch (error) {
    console.error('Error translating provider:', error);
    return {
      success: false,
      error: error.message
    };
  }
}